import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Paper,
    Typography,
    Button,
    Grid,
    Box,
    Alert,
    Tooltip,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RunOptimizerResultTable from './RunOptimizerResultTable';
import { PortfolioDesignViewingIndicator, PortfolioDesignSelectionDetails } from './optimizer/PortfolioDesignSelection';
import { formatAsDollar, formatDecimalTo2Places } from '../utils/commonUtils';
import { normalizeCogniveId } from './optimizer/optimizerIdentityHelpers';
import * as XLSX from 'xlsx';
import OptimizerPortfolioDropdown from "./optimizer/OptimizerPortfolioDropdown";
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OptimizerConstraintsPanel from './optimizer/OptimizerConstraintsPanel';
import OptimizerRunToolbar, { formatRunTime } from './optimizer/OptimizerRunToolbar';
import { MANUAL_OBJECTIVES, MAX_SHARPE_OBJECTIVE, optimizerAlphaScorePresentation, tradeIdeaExpectedReturnPolicy } from '../utils/optimizerObjectives';
import OptimizerSaveDialog from './optimizer/OptimizerSaveDialog';
import OptimizerSearchPanel, { OptimizerSearchSettings } from './optimizer/OptimizerSearchPanel';
import useOptimizerSearch from '../hooks/useOptimizerSearch';
import { META_OPTIMIZER_ENABLED } from '../utils/portfolioDesign';
import { isSearchRateLimited } from '../utils/portfolioDesignResults';
import SecurityControlSelect from './optimizer/SecurityControlSelect';
import { useOptimizerRuleActions } from './optimizer/useOptimizerRuleActions';
import { baselineColumnLabel } from './optimizer/optimizerRuleRegistry';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import {
    baselineInclusionState,
    baselineMembersLabel,
    includeBaselineMembers,
} from './optimizer/optimizerBaselineInclusion';
import {
    posSizingActionButtonSx,
    posSizingTableTitleSx,
} from './pos_sizing_table/tableStyles';
import { cardClasses, negativeCell } from '../styles/tailwindClasses';
import { analyticsPanelSx } from '../styles/analyticsMsiStyles';
import {
    securityIdentityPresentation,
    securitySubmittedIdentifier,
    customerSecurityText,
} from '../utils/securityIdentityPresentation';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from './commonComponents/SecurityIdentityFieldSelector';
import BusyOverlay from './commonComponents/BusyOverlay';
import DataTable from './data_table/DataTable';
import { sortByAbsoluteNumber, sortByNumber, sortByText } from './data_table/sorting';
import { optimizedCellClassName } from './data_table/tableClasses';
import KitTooltip from './ui/Tooltip';
import { cx } from './ui/classNames';
import OptimizerWatchlistRestorationDialog from './optimizer/OptimizerWatchlistRestorationDialog';
import {
    WORKSPACE_DIMENSIONS,
    optimizerWorkspaceGridSx,
    optimizerWorkspacePanelSx,
    workspaceActionClusterSx,
    workspacePanelDescriptionSx,
    workspacePanelHeadingSx,
} from '../styles/workspaceChromeStyles';
import DashboardToolbarPortal from './dashboard/components/DashboardToolbarPortal';

const OPTIMIZER_STACK_HEIGHT = WORKSPACE_DIMENSIONS.optimizerStackHeight;
const NO_SECURITY_CONTROLS = Object.freeze({});
const NO_ROWS = Object.freeze([]);
// The Pos Sizing baseline is marked orange-gold; Current keeps the primary blue.
// The C/PS switch and the checked-row tint share the colour.
const posSizingAccent = (theme) => (
    theme.palette.mode === 'dark' ? '#f0b44c' : '#d9982a'
);
// Checked rows take the selected tint under the Current baseline (the
// table's own ticked-row look). Under Pos Sizing they take the accent
// (11% light, 16% dark) laid over the surface colour instead, so striped
// rows read the same.
const POS_SIZING_CHECKED_ROW_CLASS_NAME = '!bg-ui-surface '
    + 'bg-[image:linear-gradient(rgb(217_152_42/0.11),rgb(217_152_42/0.11))] '
    + 'dark:bg-[image:linear-gradient(rgb(240_180_76/0.16),rgb(240_180_76/0.16))]';
// "View names" in the portfolio rules marks the names the run changed: a
// 4px warning bar on the row's left edge and a 9% warning tint over the
// row's own background. `data-names-changed` marks them for tests and tools.
const CHANGED_NAME_ROW_PROPS = Object.freeze({
    'data-names-changed': '',
    className: '[&>td:first-child]:shadow-[inset_4px_0_theme(colors.ui.warning)] '
        + '!bg-[image:linear-gradient(color-mix(in_srgb,var(--app-warning)_9%,transparent),'
        + 'color-mix(in_srgb,var(--app-warning)_9%,transparent))]',
});
// Values from a result that no longer matches the configuration are dimmed.
const STALE_CELL_STYLE = Object.freeze({ opacity: 0.56 });
const STALE_HEADER_CLASS_NAME = '[&>div]:opacity-[0.56]';

// Rows keep the grid's historical key: id, else SEDOL, else ticker, else
// position. The checkbox column reads and writes `checkBoxEnable` by it.
const getOptimizerRowId = (row, index) => row?.id ?? row?.sedol ?? row?.ticker ?? index;
const negativeClassName = ({ getValue }) => (getValue() < 0 ? negativeCell : undefined);
const formatDollarCell = ({ getValue }) => formatAsDollar(getValue());
const formatDecimalCell = ({ getValue }) => formatDecimalTo2Places(getValue());

function OptimizerBaselineSwitch({
    value,
    onChange,
    disabled = false,
    disabledTooltip = '',
}) {
    const options = [
        {
            value: 'normal',
            shortLabel: 'C',
            ariaLabel: 'Current',
            tooltip: 'Current portfolio baseline',
        },
        {
            value: 'pos-sizing',
            shortLabel: 'PS',
            ariaLabel: 'Pos Sizing',
            tooltip: 'Position Sizing portfolio baseline',
        },
    ];

    const switchControl = (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={value}
            onChange={(_event, newValue) => {
                if (!newValue || disabled) return;
                onChange?.(newValue);
            }}
            aria-label="Portfolio baseline"
            sx={{
                height: 34,
                p: '3px',
                gap: '3px',
                border: 1,
                borderColor: 'divider',
                borderRadius: '999px',
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.045),
                boxShadow: (theme) => `inset 0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
                '& .MuiToggleButtonGroup-grouped': {
                    m: 0,
                    border: 0,
                    borderRadius: '999px !important',
                },
                '& .MuiToggleButton-root': {
                    minWidth: 38,
                    height: 26,
                    px: 1,
                    color: 'text.secondary',
                    fontFamily: '"Space Grotesk", "Inter", "Roboto", sans-serif',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: '0.055em',
                    transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                    '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09),
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: 'primary.contrastText',
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        boxShadow: (theme) => `0 2px 7px ${alpha(theme.palette.primary.main, 0.32)}`,
                        transform: 'translateY(-0.5px)',
                    },
                    '&.Mui-disabled': {
                        border: 0,
                    },
                },
                '& .MuiToggleButton-root.optimizer-baseline-pos-sizing:hover': {
                    bgcolor: (theme) => alpha(posSizingAccent(theme), 0.16),
                },
                '& .MuiToggleButton-root.optimizer-baseline-pos-sizing.Mui-selected, & .MuiToggleButton-root.optimizer-baseline-pos-sizing.Mui-selected:hover': {
                    color: '#3a2600',
                    background: (theme) => `linear-gradient(135deg, ${alpha(posSizingAccent(theme), 0.72)}, ${posSizingAccent(theme)})`,
                    boxShadow: (theme) => `0 2px 7px ${alpha(posSizingAccent(theme), 0.4)}`,
                },
            }}
        >
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    value={option.value}
                    aria-label={option.ariaLabel}
                    disabled={disabled}
                    className={option.value === 'pos-sizing' ? 'optimizer-baseline-pos-sizing' : undefined}
                >
                    <Tooltip title={option.tooltip} arrow>
                        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%' }}>
                            {option.shortLabel}
                        </Box>
                    </Tooltip>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );

    if (!disabledTooltip) return switchControl;
    return (
        <Tooltip title={disabledTooltip} arrow>
            <span>{switchControl}</span>
        </Tooltip>
    );
}

const CHANGE_CELL_CLASS_NAME = 'flex w-full items-center justify-center gap-1';

// The change from the run's baseline. A name outside the run, a newly opened
// name and a name held at zero each read differently from "no change".
function OptimizerChangeCell({ row, value, resultShown }) {
    if (row?.optimalGmv == null) {
        return resultShown
            ? <span className={cx(CHANGE_CELL_CLASS_NAME, 'text-ui-disabled-text')}>Not in run</span>
            : null;
    }
    if (Number(row?.runBaselineGmv) === 0) {
        return Number(row.optimalGmv) === 0
            ? <span className={cx(CHANGE_CELL_CLASS_NAME, 'text-content-secondary')}>—</span>
            : <span className={cx(CHANGE_CELL_CLASS_NAME, 'font-bold')}>New</span>;
    }
    const percent = (value ?? 0) * 100;
    return (
        <span
            className={cx(
                CHANGE_CELL_CLASS_NAME,
                percent !== 0 ? 'font-bold' : 'font-medium',
                percent > 0 && 'text-positive',
                percent < 0 && 'text-negative',
            )}
        >
            {percent > 0 && <span className="text-[0.75rem]">▲</span>}
            {percent < 0 && <span className="text-[0.75rem]">▼</span>}
            {formatDecimalTo2Places(percent)}
        </span>
    );
}

OptimizerChangeCell.propTypes = {
    row: PropTypes.object,
    value: PropTypes.number,
    resultShown: PropTypes.bool,
};

function optimizerIdentityPresentation(row, field) {
    const presentation = securityIdentityPresentation(row, field);
    if (field !== 'company_name' || presentation.value !== '—') {
        return presentation;
    }
    const ticker = securityIdentityPresentation(row, 'ticker');
    return ticker.value === '—'
        ? presentation
        : {
            value: ticker.value,
            tooltip: 'Company name is unavailable in this saved result; showing its receipt ticker.',
        };
}

export default function PortfolioOptimizerTable({
    setConstraints,
    riskMetricsActPort,
    riskMetricsOptPort,
    riskMetricsDeltaPort,
    displayedExpectedReturnPolicy = null,
    maxSharpeSkill = null,
    setMaxSharpeSkill = null,
    error,
    optimizerQueueState,
    cancelQueuedOptimization,
    setError,
    optimizerIsRunning,
    optimizerRunBlocker = null,
    runOptimization,
    metaOptimizer = null,
    constraints,
    gmvBudget = null,
    groupConstraints = [],
    setGroupConstraints = () => {},
    optimizerV2Capability = null,
    optimizerAttributeCatalog = null,
    handleWhatIfPortfolioChange,
    optimalPortfolioOptimizer,
    setOptimalPortfolioOptimizer,
    optimalPortfolioSummary,
    setOptimalPortfolioSummary,
    listOfWhatIfPortfolios,
    saveOptimizationAndWhatIfPortfolioData,
    constraintToggles,
    setConstraintToggles,
    showPortfolioMetrics,
    tickerData,
    setTickerData,
    objective,
    setObjective,
    fetchOptimizerPortfolios,
    setErrorOptimization,
    setShowPortfolioMetrics,
    clearOptimizedOverlay,
    unsavedOptimizerRun,
    receiptDisplayActive,
    resultStale = false,
    detachOptimizerResult,
    resetOptimizerLimits,
    setOptimizedCalculations,
    optStatus,
    viewMode,
    setViewMode,
    risksByFactor,
    constraintEvaluation = null,
    resultIssuedAt = null,
    solverStatus = null,
    factorNames,
    handleAddConstraint,
    handleRemoveConstraint,
    matricesLoading,
    viewModeLoading,
    finalTickerDataFlag = false,
    resetOptimizerDefaults,
    refreshSavedOptimizedPortfolio,
    posSizingSyncStatus = null,
    onSyncPosSizingDraft = null,
    posSizingContextControl = null,
    onWatchlistRestorationStaged = () => {},
}) {
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const [dismissedCompatibilityKey, setDismissedCompatibilityKey] = useState('');
    const [restorationPortfolio, setRestorationPortfolio] = useState(null);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [highlightChangedNames, setHighlightChangedNames] = useState(false);
    const optimizerMetadataReady = Boolean(
        optimizerV2Capability && optimizerAttributeCatalog,
    );
    const selectedSavedPortfolio = useMemo(
        () => (listOfWhatIfPortfolios || []).find((portfolio) => (
            portfolio?.value === optimalPortfolioOptimizer
        )) ?? null,
        [listOfWhatIfPortfolios, optimalPortfolioOptimizer],
    );
    const selectedCompatibility = selectedSavedPortfolio?.compatibility ?? null;
    const compatibilityKey = selectedCompatibility?.reason === 'watchlist_membership_changed'
        ? [
            selectedSavedPortfolio?.id,
            selectedSavedPortfolio?.portfolioVersion,
            selectedCompatibility?.current_watchlist_revision_id,
            selectedCompatibility?.source_watchlist_revision_id,
        ].join(':')
        : '';
    const showCompatibilityNotice = Boolean(
        compatibilityKey && dismissedCompatibilityKey !== compatibilityKey,
    );

    useEffect(() => {
        setRestorationPortfolio(null);
    }, [compatibilityKey]);
    const handleRestorationPortfolioDeleted = useCallback(() => {
        setRestorationPortfolio(null);
        setDismissedCompatibilityKey('');
        clearOptimizedOverlay?.();
        setOptimalPortfolioOptimizer('');
        setOptimalPortfolioSummary('');
        setShowPortfolioMetrics(false);
        setOptimizedCalculations?.(null);
        resetOptimizerDefaults?.();
    }, [
        clearOptimizedOverlay,
        resetOptimizerDefaults,
        setOptimalPortfolioOptimizer,
        setOptimalPortfolioSummary,
        setOptimizedCalculations,
        setShowPortfolioMetrics,
    ]);
    const attributeDefinitions = useMemo(
        () => optimizerAttributeCatalog?.definitions ?? [],
        [optimizerAttributeCatalog],
    );
    const [constraintScope, setConstraintScope] = useState('portfolio');
    const metaAvailable = META_OPTIMIZER_ENABLED && metaOptimizer?.design != null;
    const search = useOptimizerSearch({ bridge: metaOptimizer?.design, enabled: metaAvailable, objective, runSingle: runOptimization, currentPortfolioLoaded: receiptDisplayActive });

    const ruleActions = useOptimizerRuleActions({
        constraints,
        setConstraints,
        setConstraintToggles,
        groupConstraints,
        setGroupConstraints,
        handleAddConstraint,
        handleRemoveConstraint,
        groupRecordLimit: optimizerV2Capability?.limits?.group_constraint_records ?? 0,
        onError: setError,
        onGmvBudgetEdited: gmvBudget?.markManual ?? null,
    });

    const [topTableHydrating, setTopTableHydrating] = useState(true);
    const optimizerBaselineEmpty = finalTickerDataFlag
        && Array.isArray(tickerData)
        && tickerData.length === 0;
    const optimizerTopTableReady = useMemo(() => (
        finalTickerDataFlag && Array.isArray(tickerData) && (
            tickerData.length === 0
            || tickerData.some((row) => (
                row?.checkBoxEnable === true
                || (Number.isFinite(Number(row?.positionGmv)) && Math.abs(Number(row.positionGmv)) > 0)
            ))
        )
    ), [finalTickerDataFlag, tickerData]);
    const canSaveOptimizerResult = Boolean(unsavedOptimizerRun);
    const savedReceiptBaselineLoaded = !canSaveOptimizerResult && (listOfWhatIfPortfolios || []).some(
        (portfolio) => (
            portfolio?.value === optimalPortfolioOptimizer
            && portfolio?.receiptStatus === 'available'
        ),
    );
    const savedReceiptProjectionPending = savedReceiptBaselineLoaded && !receiptDisplayActive;
    const optimizerUiLoading = savedReceiptProjectionPending || (!optimizerBaselineEmpty && (
        optimizerIsRunning || viewModeLoading || matricesLoading || topTableHydrating
    ));
    const baselineSwitchLoading = Boolean(
        savedReceiptProjectionPending || viewModeLoading || matricesLoading || topTableHydrating
    );
    const baselineInclusion = useMemo(() => baselineInclusionState(tickerData), [tickerData]);
    const baselineMembers = baselineMembersLabel(viewMode, baselineInclusion.memberCount);
    const restoreIncludedDisabled = baselineSwitchLoading
        || Boolean(optimizerIsRunning)
        || baselineInclusion.memberCount === 0
        || baselineInclusion.matches;
    let restoreIncludedTooltip = `Restore included securities to the ${baselineMembers}`;
    if (baselineSwitchLoading) {
        restoreIncludedTooltip = 'Wait for the portfolio baseline to finish loading.';
    } else if (optimizerIsRunning) {
        restoreIncludedTooltip = 'Wait for the optimization to finish.';
    } else if (baselineInclusion.memberCount === 0) {
        restoreIncludedTooltip = viewMode === 'pos-sizing'
            ? 'The Pos Sizing portfolio has no positions to restore.'
            : 'The Current portfolio has no holdings to restore.';
    } else if (baselineInclusion.matches) {
        restoreIncludedTooltip = `Included securities match the ${baselineMembers}.`;
    }
    // A checkbox edit like any other: a shown result is kept and marked out of date.
    const restoreIncludedSecurities = useCallback(() => {
        setTickerData(includeBaselineMembers);
    }, [setTickerData]);
    const optimizerCardSx = {
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    };
    const baselineLabel = baselineColumnLabel(constraintEvaluation, viewMode);
    const cardinalityEvaluation = constraintEvaluation?.rules?.find(
        (rule) => rule?.rule_id === 'cardinality:max_names_changed',
    ) ?? null;
    const cardinalityAvailable = optimizerV2Capability?.cardinality?.enabled === true
        || cardinalityEvaluation != null;
    const changedNameIds = useMemo(
        () => new Set(constraintEvaluation?.portfolio?.names_changed?.cognive_ids ?? []),
        [constraintEvaluation],
    );
    const objectiveUnavailable = objective !== 'AlphaScore' && objective !== MAX_SHARPE_OBJECTIVE && !(META_OPTIMIZER_ENABLED && (objective in MANUAL_OBJECTIVES || (objective === 'PortfolioDesign' && constraints.design)));
    const securityControls = constraints?.security_controls ?? NO_SECURITY_CONTROLS;
    // Free removes the control; Hold and No short are kept per Cognive ID.
    const setSecurityControl = useCallback((cogniveId, control) => {
        setConstraints((previous) => {
            const next = { ...(previous.security_controls ?? {}) };
            if (control === 'free') delete next[cogniveId];
            else next[cogniveId] = control;
            return { ...previous, security_controls: next };
        });
    }, [setConstraints]);
    // Only checked names are run, so only they count toward a control.
    const securityControlCounts = useMemo(() => {
        const counts = { hold: 0, no_short: 0 };
        (tickerData || []).forEach((row) => {
            if (row?.checkBoxEnable !== true) return;
            const control = securityControls[normalizeCogniveId(row?.cognive_id)];
            if (Object.prototype.hasOwnProperty.call(counts, control)) counts[control] += 1;
        });
        return counts;
    }, [securityControls, tickerData]);
    const excludedHoldings = useMemo(() => {
        if (!receiptDisplayActive) return null;
        const rows = (tickerData || []).filter((row) => {
            const baseline = Number(row?.positionGmv);
            return row?.optimalGmv == null && Number.isFinite(baseline) && baseline !== 0;
        });
        return rows.length
            ? {
                count: rows.length,
                gmv: rows.reduce((sum, row) => sum + Math.abs(Number(row.positionGmv)), 0),
            }
            : null;
    }, [receiptDisplayActive, tickerData]);
    const runStatus = (() => {
        if (search.error || error) {
            return {
                severity: !search.error && [
                    'INFEASIBLE',
                    'UNBOUNDED',
                    'NO_SOLUTION_BEFORE_LIMIT',
                ].includes(optStatus) ? 'warning' : 'error',
                message: search.error || error,
            };
        }
        if (search.active) {
            const study = search.model.study;
            const rateLimited = isSearchRateLimited(study);
            return {
                severity: 'info',
                message: study.status === 'cancel_requested' ? 'Stopping search…'
                    : rateLimited ? 'Search paused due to rate limit'
                        : study.preparing ? 'Preparing search…' : 'Search in progress',
                detail: rateLimited ? `Retrying automatically in 30 seconds · ${study.next_trial ?? 0} / ${study.trial_budget} jobs finished`
                    : study.preparing ? null : `${study.next_trial ?? 0} / ${study.trial_budget} jobs finished`,
            };
        }
        if (search.model.busy) return { severity: 'info', message: 'Loading optimizer data…' };
        if (optimizerIsRunning) return { severity: 'info', message: 'Working…' };
        if (resultStale) {
            return {
                severity: 'stale',
                message: 'Configuration changed — rerun to update results',
            };
        }
        if (optStatus === 'SUCCESS') {
            return {
                severity: 'success',
                message: savedReceiptBaselineLoaded
                    ? 'Saved result'
                    : 'Optimization successful',
                detail: [
                    formatRunTime(resultIssuedAt),
                    solverStatus?.outcome === 'feasible_not_proven'
                        ? 'Portfolio meets your limits'
                        : null,
                ].filter(Boolean).join(' · ') || null,
            };
        }
        return null;
    })();

    useEffect(() => {
        if (matricesLoading || viewModeLoading || !finalTickerDataFlag) {
            setTopTableHydrating(true);
            return;
        }

        if (optimizerTopTableReady) {
            setTopTableHydrating(false);
        }
    }, [
        finalTickerDataFlag,
        matricesLoading,
        optimizerTopTableReady,
        viewModeLoading,
    ]);

    useEffect(() => {
        setHighlightChangedNames(false);
    }, [constraintEvaluation]);


    // Numbers keep the grid's look: trade ideas right-aligned, other values
    // left-aligned, negatives red; result columns dim while stale.
    const columns = useMemo(() => {
        const identityLabel = securityIdentityFieldLabel(identityDisplayField);
        const staleStyle = () => (resultStale ? STALE_CELL_STYLE : undefined);
        const numberColumn = (id, header, size, meta = {}) => ({
            id,
            header,
            size,
            accessorFn: (row) => row?.[id],
            sortFn: sortByNumber,
            cell: formatDollarCell,
            meta,
        });
        return [
            {
                id: 'identity_summary',
                header: identityLabel,
                size: 250,
                accessorFn: (row) => optimizerIdentityPresentation(row, identityDisplayField).value,
                sortFn: sortByText,
                cell: ({ row: { original } }) => {
                    const presentation = optimizerIdentityPresentation(original, identityDisplayField);
                    return (
                        <KitTooltip content={presentation.tooltip || presentation.value}>
                            <span
                                aria-label={`${identityLabel}: ${presentation.value}`}
                                className="block truncate"
                            >
                                {presentation.value}
                            </span>
                        </KitTooltip>
                    );
                },
                meta: {
                    cellClassName: identityDisplayField === 'submitted_identifier' ? 'font-mono' : undefined,
                    headerControls: () => (
                        <SecurityIdentityFieldMenu
                            value={identityDisplayField}
                            onChange={setIdentityDisplayField}
                        />
                    ),
                },
            },
            {
                ...numberColumn('trade_idea', 'Trade idea', 118, {
                    align: 'right',
                    cellClassName: negativeClassName,
                }),
                sortFn: sortByAbsoluteNumber,
                cell: formatDecimalCell,
            },
            numberColumn(
                'positionGmv',
                `${viewMode === 'pos-sizing' ? 'Pos Sizing' : 'Current'} position ($)`,
                158,
                { cellClassName: negativeClassName },
            ),
            numberColumn('optimalGmv', `Optimized position ($)${resultStale ? ' · previous' : ''}`, 172, {
                cellClassName: optimizedCellClassName,
                cellStyle: staleStyle,
                headerClassName: cx(optimizedCellClassName, resultStale && STALE_HEADER_CLASS_NAME),
            }),
            numberColumn('tradeGmv', 'Trade ($)', 128, {
                cellClassName: negativeClassName,
                cellStyle: staleStyle,
            }),
            {
                ...numberColumn('deltaGmv', 'Change (%)', 112, { cellStyle: staleStyle }),
                cell: ({ row: { original }, getValue }) => (
                    <OptimizerChangeCell
                        row={original}
                        value={getValue()}
                        resultShown={Boolean(receiptDisplayActive)}
                    />
                ),
            },
            {
                id: 'tradingControl',
                header: 'Trading',
                size: 104,
                enableSorting: false,
                accessorFn: (row) => securityControls[normalizeCogniveId(row?.cognive_id)] ?? 'free',
                cell: ({ row: { original }, getValue }) => {
                    const cogniveId = normalizeCogniveId(original?.cognive_id);
                    if (!cogniveId) return null;
                    return (
                        <SecurityControlSelect
                            cogniveId={cogniveId}
                            ticker={original?.ticker ?? null}
                            value={getValue()}
                            checked={original?.checkBoxEnable === true}
                            disabled={Boolean(optimizerIsRunning) || search.busy}
                            onChange={setSecurityControl}
                        />
                    );
                },
                meta: {
                    wrap: true,
                    description: 'Hold keeps the baseline position; No short keeps it long or flat. '
                        + 'Applies to checked names with Max Alpha or Max Sharpe.',
                },
            },
            { ...numberColumn('beta', 'Beta', 92), cell: formatDecimalCell },
            numberColumn('factorRiskDollar', 'Factor Risk ($)', 132, { cellClassName: negativeClassName }),
            numberColumn('idioRiskDollar', 'Idio Risk ($)', 132, { cellClassName: negativeClassName }),
            { ...numberColumn('idio_vol_annual_custom', 'Idio Vol (custom) (%)', 148), cell: formatDecimalCell },
            { ...numberColumn('idio_vol_linked', 'Idio Vol (linked) (%)', 142), cell: formatDecimalCell },
            { ...numberColumn('factorVol', 'Factor Vol (%)', 126), cell: formatDecimalCell },
        ];
    }, [
        identityDisplayField,
        optimizerIsRunning,
        receiptDisplayActive,
        resultStale,
        search.busy,
        securityControls,
        setSecurityControl,
        viewMode,
    ]);

    // The checkboxes show and set each row's `checkBoxEnable`: the selection
    // is read from the rows on every render, and a change writes every row
    // back with its new flag (nothing changes while the optimizer runs).
    const tickerRows = Array.isArray(tickerData) ? tickerData : NO_ROWS;
    const selectionReadOnly = Boolean(optimizerIsRunning);
    const rowSelection = useMemo(() => {
        const selection = {};
        tickerRows.forEach((row, index) => {
            if (row?.checkBoxEnable) selection[String(getOptimizerRowId(row, index))] = true;
        });
        return selection;
    }, [tickerRows]);
    const handleRowSelectionChange = (nextSelection) => {
        if (selectionReadOnly) return;
        setTickerData(tickerRows.map((row, index) => ({
            ...row,
            checkBoxEnable: Boolean(nextSelection[String(getOptimizerRowId(row, index))]),
        })));
    };
    const getRowClassName = (row) => (
        row?.checkBoxEnable && viewMode === 'pos-sizing' ? POS_SIZING_CHECKED_ROW_CLASS_NAME : undefined
    );
    const getRowProps = (row) => (
        highlightChangedNames && changedNameIds.has(normalizeCogniveId(row?.cognive_id))
            ? CHANGED_NAME_ROW_PROPS
            : undefined
    );
    // Skeleton rows while the first rows load; a spinner over rows already
    // shown. A settled empty baseline keeps decorative striped rows.
    const showSkeletonRows = optimizerUiLoading && tickerRows.length === 0 && !optimizerBaselineEmpty;

    const handleDownload = () => {
        if (savedReceiptProjectionPending) return;

        const exportData = tickerData.map((row) => {
            return ({
            "Identifier": securitySubmittedIdentifier(row, { includeTickerType: true }),
            "Ticker": customerSecurityText(row.ticker, row) || securityIdentityPresentation(row, 'ticker').value,
            "Trade idea": row.trade_idea !== undefined ? row.trade_idea : "",
            [`${viewMode === 'pos-sizing' ? 'Pos Sizing' : 'Current'} position ($)`]:
                row.positionGmv !== undefined ? formatAsDollar(row.positionGmv) : "",
            "Optimized position ($)": row.optimalGmv !== undefined ? formatAsDollar(row.optimalGmv) : "",
            "Trade ($)": row.tradeGmv !== undefined ? formatAsDollar(row.tradeGmv) : "",
            "Change (%)": row.deltaGmv != null ? formatDecimalTo2Places(row.deltaGmv * 100) : "",
            "Beta": row.beta !== undefined ? formatDecimalTo2Places(row.beta) : "",
            "Factor Risk ($)": row.factorRiskDollar !== undefined ? formatAsDollar(row.factorRiskDollar) : "",
            "Idio Risk ($)": row.idioRiskDollar !== undefined ? formatAsDollar(row.idioRiskDollar) : "",
            "Idio Vol (custom) (%)": row.idio_vol_annual_custom !== undefined ? formatDecimalTo2Places(row.idio_vol_annual_custom) : "",
            "Idio Vol (linked) (%)": row.idio_vol_linked !== undefined ? formatDecimalTo2Places(row.idio_vol_linked) : "",
            "Factor Vol (%)": row.factorVol !== undefined ? formatDecimalTo2Places(row.factorVol) : "",
            });
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Optimizer Tickers');
        XLSX.writeFile(wb, 'optimizer_tickers.xlsx');

    };

    const alphaScorePresentation = optimizerAlphaScorePresentation({
        receiptDisplayActive, receiptObjective: constraintEvaluation?.objective,
        objective, design: constraints?.design,
    });
    const optimizationOutcome = (<>
        <Box sx={{ ...workspacePanelHeadingSx, mb: 1 }}>
            <Typography variant="subtitle1" sx={posSizingTableTitleSx}>
                Optimization Outcome
            </Typography>
            {metaAvailable && <PortfolioDesignViewingIndicator selection={search.selection} hasSearch={Boolean(search.model.study)}
                savedPortfolioName={unsavedOptimizerRun ? null : selectedSavedPortfolio?.value} />}
            <Typography sx={workspacePanelDescriptionSx}>
                Compare baseline and optimized portfolio metrics for the portfolio displayed above.
            </Typography>
        </Box>
        <RunOptimizerResultTable
            riskMetricsActPort={riskMetricsActPort}
            riskMetricsOptPort={riskMetricsOptPort}
            riskMetricsDeltaPort={riskMetricsDeltaPort}
            showPortfolioMetrics={showPortfolioMetrics}
            optimizerIsRunning={optimizerIsRunning}
            viewModeLoading={viewModeLoading || matricesLoading || (!receiptDisplayActive && Boolean(metaOptimizer?.design?.baselineLoading))}
            baselineLabel={baselineLabel}
            constraintEvaluation={constraintEvaluation}
            resultStale={resultStale}
            excludedHoldings={excludedHoldings}
            expectedReturnPolicy={displayedExpectedReturnPolicy}
            alphaScoreLabel={alphaScorePresentation.label}
            alphaScoreTooltip={alphaScorePresentation.tooltip}
            hideTitle
        />
    </>);

    return (
        <>
            <Grid item xs={12} md={12} mb={2}>
                <Paper className={cardClasses} elevation={0} sx={[{ p: 2, ...optimizerCardSx }, analyticsPanelSx]}>
                    <DashboardToolbarPortal>
                        <Box
                            sx={{
                                ...workspaceActionClusterSx,
                                ml: 0,
                                flexWrap: 'nowrap',
                                gap: 0.75,
                            }}
                        >
                            {posSizingContextControl}
                            <Box sx={{ width: { xs: '100%', sm: 260 } }}>
                                <OptimizerPortfolioDropdown
                                    optimalPortfolioOptimizer={optimalPortfolioOptimizer}
                                    optimalPortfolioSummary={optimalPortfolioSummary}
                                    setOptimalPortfolioSummary={setOptimalPortfolioSummary}
                                    setOptimalPortfolioOptimizer={setOptimalPortfolioOptimizer}
                                    handleWhatIfPortfolioChange={handleWhatIfPortfolioChange}
                                    listOfWhatIfPortfolios={listOfWhatIfPortfolios}
                                    fetchOptimizerPortfolios={fetchOptimizerPortfolios}
                                    setErrorOptimization={setError}
                                    setShowPortfolioMetrics={setShowPortfolioMetrics}
                                    clearOptimizedOverlay={clearOptimizedOverlay}
                                    unsavedOptimizerRun={unsavedOptimizerRun}
                                    setOptimizedCalculations={setOptimizedCalculations}
                                    dropdownDisplayValue={optimalPortfolioOptimizer}
                                    restrictedPortfolioNames={['pos-sizing']}
                                    restrictedDeletePortfolioNames={['pos-sizing']}
                                    refreshSavedOptimizedPortfolio={refreshSavedOptimizedPortfolio}
                                    posSizingSyncStatus={posSizingSyncStatus}
                                    onSyncPosSizingDraft={onSyncPosSizingDraft}
                                    onWatchlistRestorationRequired={setRestorationPortfolio}
                                    onWatchlistRestorationStaged={onWatchlistRestorationStaged}
                                    handleSelectedOptimizedPortfolio={() => {
                                        resetOptimizerDefaults?.();
                                    }}
                                    hideTitle
                                    compact
                                />
                            </Box>
                            <OptimizerBaselineSwitch
                                value={viewMode}
                                onChange={setViewMode}
                                disabled={baselineSwitchLoading}
                                disabledTooltip={baselineSwitchLoading
                                    ? 'Wait for the portfolio baseline to finish loading.'
                                    : ''}
                            />
                            <Tooltip title={restoreIncludedTooltip}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={restoreIncludedSecurities}
                                        aria-label="Restore included securities to baseline"
                                        disabled={restoreIncludedDisabled}
                                        sx={posSizingActionButtonSx}
                                    >
                                        <RestartAltRoundedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {(viewModeLoading || matricesLoading) && (
                                <Box
                                    role="status"
                                    aria-live="polite"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.6,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <CircularProgress size={14} thickness={5} />
                                    <Typography variant="caption">
                                        Loading {viewMode === 'normal' ? 'Current' : 'Pos Sizing'} baseline…
                                    </Typography>
                                </Box>
                            )}
                            <Tooltip title={savedReceiptProjectionPending
                                ? 'Wait for the saved receipt result to finish loading.'
                                : 'Download optimizer portfolio'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={handleDownload}
                                        aria-label="Download optimizer portfolio"
                                        disabled={savedReceiptProjectionPending}
                                        sx={posSizingActionButtonSx}
                                    >
                                        <FileDownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </DashboardToolbarPortal>
                    {showCompatibilityNotice && (
                        <Alert
                            severity="warning"
                            sx={{ mb: 1.5 }}
                            action={(
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Button
                                        color="inherit"
                                        size="small"
                                        disabled={
                                            selectedCompatibility?.restoration_status !== 'preview_required'
                                        }
                                        onClick={() => {
                                            setError?.(null);
                                            setRestorationPortfolio(selectedSavedPortfolio);
                                        }}
                                    >
                                        Recover
                                    </Button>
                                    <IconButton
                                        aria-label="Dismiss compatibility notice"
                                        color="inherit"
                                        size="small"
                                        onClick={() => setDismissedCompatibilityKey(compatibilityKey)}
                                    >
                                        <CloseRoundedIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            )}
                        >
                            This saved result is partially available: {' '}
                            {selectedCompatibility.missing_security_count} of {' '}
                            {selectedCompatibility.saved_security_count} saved securities {' '}
                            are not in the current Watchlist. The saved run metrics remain {' '}
                            available; recover the portfolio before loading or re-optimizing it.
                            {selectedCompatibility.restoration_status !== 'preview_required' && (
                                <> The original submitted identifiers are no longer available; add them manually.</>
                            )}
                        </Alert>
                    )}
                    <BusyOverlay
                        show={optimizerUiLoading && !showSkeletonRows}
                        minHeight={WORKSPACE_DIMENSIONS.primaryTableHeight}
                        spinnerSize={30}
                    >
                        <DataTable
                            aria-label="Optimizer securities"
                            data={tickerRows}
                            columns={columns}
                            getRowId={getOptimizerRowId}
                            rowSelection={rowSelection}
                            onRowSelectionChange={handleRowSelectionChange}
                            enableRowSelection={!selectionReadOnly}
                            getRowClassName={getRowClassName}
                            getRowProps={getRowProps}
                            loading={showSkeletonRows}
                            emptyRowCount={Math.ceil((WORKSPACE_DIMENSIONS.primaryTableHeight - 40) / 38)}
                            height={WORKSPACE_DIMENSIONS.primaryTableHeight}
                            rowHeight={38}
                            headerHeight={40}
                            virtualize
                        />
                    </BusyOverlay>
                </Paper>
            </Grid>
            <OptimizerRunToolbar
                objective={objective}
                design={constraints.design}
                onObjectiveChange={(value) => setObjective(value, maxSharpeSkill)}
                expectedReturnSkill={constraints.expected_return_policy?.information_coefficient ?? null}
                onExpectedReturnSkillChange={(value) => {
                    setConstraints((previous) => ({
                        ...previous, expected_return_policy: tradeIdeaExpectedReturnPolicy(value),
                    }));
                    // Remembered for this member's next Max Sharpe runs.
                    setMaxSharpeSkill?.(value);
                }}
                aum={constraints?.static_constraints?.aum ?? null}
                onAumChange={(value) => ruleActions.setPortfolioRuleValue('aum', value)}
                inputsDisabled={Boolean(optimizerIsRunning) || search.busy}
                onRun={async () => { const result = await search.run(); if (result?.study_id) setConstraintScope('search'); }}
                runDisabled={optimizerUiLoading || search.busy || Boolean(optimizerRunBlocker) || objectiveUnavailable
                    || Boolean(metaOptimizer?.design?.baselineLoading)
                    || (!search.isSearch && Boolean(unsavedOptimizerRun && metaOptimizer?.design?.currentResult?.kind !== 'meta'))}
                runTooltip={search.active ? 'A search is in progress. Open Search to review progress or stop it.'
                    : optimizerRunBlocker || (optimizerUiLoading || search.model.busy || metaOptimizer?.design?.baselineLoading
                    ? 'Loading optimizer data' : search.isSearch ? 'Search the enabled constraint ranges' : 'Run optimization')}
                executionDescription={metaAvailable ? search.active
                    ? 'Open Search to review progress, inspect portfolios, or stop and keep results.'
                    : search.isSearch
                    ? `Run searches ${search.dimensionCount} constraint range${search.dimensionCount === 1 ? '' : 's'}; fixed limits stay unchanged.`
                    : objective === MAX_SHARPE_OBJECTIVE ? null
                    : 'Run solves once. Set a constraint to Explore to search a range of limits.' : null}
                onSave={() => setSaveDialogOpen(true)}
                saveDisabled={optimizerUiLoading || !canSaveOptimizerResult}
                showNewRun={Boolean(receiptDisplayActive || search.model.study)}
                onNewRun={async () => {
                    if (search.model.study) await search.model.newStudy();
                    else await detachOptimizerResult?.();
                }}
                newRunDisabled={optimizerUiLoading || search.busy}
                status={runStatus}
                onStatusDismiss={() => { setError?.(null); if (search.error) search.clearError(); }}
            />
            {metaOptimizer?.design?.baselineError && <Alert severity="warning" sx={{ mb: 1.5 }}
                action={<Button color="inherit" size="small" onClick={metaOptimizer.design.retryBaseline}>Retry</Button>}>
                Baseline statistics: {metaOptimizer.design.baselineError}
            </Alert>}
            {optimizerQueueState && <Alert severity="info" sx={{ mb: 1.5 }}
                action={<Button color="inherit" size="small" onClick={cancelQueuedOptimization}>Stop</Button>}>
                Waiting for solver capacity{optimizerQueueState.queue_position > 1 ? ` · ${optimizerQueueState.queue_position - 1} ahead` : ''}. Your run will continue automatically.
            </Alert>}
            <Box sx={optimizerWorkspaceGridSx}>
                <Box sx={{ minWidth: 0 }}>
                    <OptimizerConstraintsPanel
                        scope={constraintScope}
                        onScopeChange={setConstraintScope}
                        searchRanges={metaAvailable ? search.ranges : null}
                        rangeEditor={metaAvailable ? search.rangeEditor : null}
                        modeEditor={metaAvailable ? search.modeEditor : null}
                        searchSettings={metaAvailable && search.isSearch ? <OptimizerSearchSettings search={search} /> : null}
                        searchContent={metaAvailable ? <OptimizerSearchPanel search={search} /> : null}
                        constraints={constraints}
                        constraintToggles={constraintToggles}
                        gmvBudget={gmvBudget}
                        groupConstraints={groupConstraints}
                        factorNames={factorNames}
                        risksByFactor={risksByFactor}
                        attributeDefinitions={attributeDefinitions}
                        optimizerAttributeRiskMetrics={optimizerAttributeCatalog?.risk_results}
                        constraintEvaluation={constraintEvaluation}
                        resultStale={resultStale}
                        viewMode={viewMode}
                        actions={ruleActions}
                        groupRecordLimit={
                            optimizerV2Capability?.limits?.group_constraint_records ?? 0
                        }
                        attributeMetadataReady={optimizerMetadataReady}
                        disabled={Boolean(optimizerIsRunning) || search.busy}
                        optimizerIsRunning={optimizerIsRunning}
                        viewModeLoading={viewModeLoading || matricesLoading || (!receiptDisplayActive && Boolean(metaOptimizer?.design?.baselineLoading))}
                        onResetLimits={() => { search.resetRanges(); resetOptimizerLimits?.(); }}
                        cardinalityAvailable={cardinalityAvailable}
                        securityControlCounts={securityControlCounts}
                        objective={objective}
                        onViewChangedNames={() => setHighlightChangedNames((current) => !current)}
                        height={OPTIMIZER_STACK_HEIGHT}
                    />
                </Box>

                <Box sx={{ minWidth: 0, display: 'flex' }}>
                    <Paper
                        className={cardClasses}
                        aria-label="Optimization Outcome"
                        elevation={0}
                        sx={[{
                            ...optimizerCardSx,
                            ...optimizerWorkspacePanelSx,
                            width: '100%',
                            height: constraintScope === 'search' ? 'auto' : OPTIMIZER_STACK_HEIGHT,
                            minHeight: OPTIMIZER_STACK_HEIGHT,
                            display: 'flex',
                            flexDirection: 'column',
                        }, analyticsPanelSx]}
                    >
                        {optimizationOutcome}
                        {metaAvailable && search.model.study && constraintScope === 'search' && <PortfolioDesignSelectionDetails
                            model={search.model} selection={search.selection} stage={search.model.stage}
                            onStageChange={(stage) => search.model.choose(search.selection.candidate.index, stage)}
                            onSaveCurrent={() => setSaveDialogOpen(true)} canSaveCurrent={metaOptimizer.design.canSaveCurrent} />}
                    </Paper>
                </Box>
            </Box>

            <OptimizerSaveDialog
                open={saveDialogOpen}
                onClose={() => setSaveDialogOpen(false)}
                listOfWhatIfPortfolios={listOfWhatIfPortfolios}
                onSave={saveOptimizationAndWhatIfPortfolioData}
            />

            <OptimizerWatchlistRestorationDialog
                open={Boolean(restorationPortfolio)}
                portfolio={restorationPortfolio}
                onClose={() => setRestorationPortfolio(null)}
                onError={(message) => setError?.(message)}
                onDeleted={handleRestorationPortfolioDeleted}
                onStaged={onWatchlistRestorationStaged}
                fetchOptimizerPortfolios={fetchOptimizerPortfolios}
            />
        </>
    );
}
